<template>
  <div>
    <template v-if="fieldType == 'datetime'">
      <v-datetime-picker
        :disabled="disabled"
        :hint="hint"
        :label="label"
        :value="fieldValue"
        timePickerFormat="ampm"
        format="ddd, MMM DD YYYY, h:mma"
        @change="value => (fieldValue = value)"
        :loading="loading"
        :success-messages="successes"
        :error-messages="errors"
        :clearable="clearable"
      ></v-datetime-picker>
    </template>

    <template v-else-if="fieldType == 'date'">
      <v-flex>
        <v-menu
          ref="dateMenu"
          v-model="dateMenu"
          :close-on-content-click="false"
          :nudge-right="40"
          :return-value.sync="fieldValue"
          lazy
          transition="scale-transition"
          offset-y
          full-width
          min-width="290px"
        >
          <template slot="activator" slot-scope="{ on }">
            <v-text-field
              :disabled="disabled"
              :label="label"
              format="dddd, MMM DD YYYY"
              @change="value => (fieldValue = value)"
              :loading="loading"
              :success-messages="successes"
              :error-messages="errors"
              :hint="hint"
              persistent-hint
              v-model="fieldValue"
              prepend-icon="event"
              readonly
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker v-model="fieldValue" no-title scrollable>
            <v-spacer></v-spacer>
            <v-btn flat color="primary" @click="dateMenu = false">Cancel</v-btn>
            <v-btn flat color="primary" @click="$refs.dateMenu.save(fieldValue)"
              >OK</v-btn
            >
          </v-date-picker>
        </v-menu>
      </v-flex>
    </template>

    <template v-else-if="fieldType == 'time'">
      <v-flex>
        <v-menu
          ref="timeMenu"
          v-model="timeMenu"
          :close-on-content-click="false"
          :nudge-right="40"
          :return-value.sync="fieldValue"
          lazy
          transition="scale-transition"
          offset-y
          full-width
          min-width="290px"
        >
          <template slot="activator" slot-scope="{ on }">
            <v-text-field
              :disabled="disabled"
              :label="label"
              format="h:mma"
              @change="value => (fieldValue = value)"
              :loading="loading"
              :success-messages="successes"
              :error-messages="errors"
              :hint="hint"
              v-model="fieldValue"
              prepend-icon="access_time"
              readonly
              v-on="on"
            ></v-text-field>
          </template>
          <v-time-picker
            v-if="timeMenu"
            v-model="fieldValue"
            full-width
            @click:minute="$refs.timeMenu.save(fieldValue)"
          ></v-time-picker>
        </v-menu>
      </v-flex>
    </template>

    <template v-else-if="fieldType == 'switch'">
      <v-switch
        :disabled="disabled"
        :hint="hint"
        :label="label"
        :value="fieldValue"
        :input-value="fieldValue"
        :success-messages="successes"
        :error-messages="errors"
        @change="value => (fieldValue = value)"
        :loading="loading"
        :false-value="false"
        :true-value="true"
      >
      </v-switch>
    </template>

    <template v-else-if="fieldType == 'textarea'">
      <v-textarea
        :disabled="disabled"
        :hint="hint"
        :label="label"
        :value="fieldValue"
        :success-messages="successes"
        :error-messages="errors"
        @change="value => (fieldValue = value)"
        :loading="loading"
        :mask="mask"
        :prefix="prefix"
        :rows="rows"
        :clearable="clearable"
        :box="box"
      >
      </v-textarea>
    </template>

    <template v-else-if="fieldType == 'autocomplete'">
      <v-autocomplete
        persistent-hint
        :disabled="disabled"
        :hint="hint"
        :label="label"
        :items="items"
        :item-text="itemText"
        :item-value="itemValue"
        :value="fieldValue"
        :success-messages="successes"
        :error-messages="errors"
        @change="value => (fieldValue = value)"
        :loading="loading"
        :mask="mask"
        :prefix="prefix"
        :clearable="clearable"
      >
      </v-autocomplete>
    </template>

    <template v-else>
      <v-text-field
        :type="type"
        persistent-hint
        :hint="hint"
        :disabled="disabled"
        :label="label"
        :value="fieldValue"
        :success-messages="successes"
        :error-messages="errors"
        @change="value => (fieldValue = value)"
        :loading="loading"
        :mask="mask"
        :prefix="prefix"
        :clearable="clearable"
      >
      </v-text-field>
    </template>
  </div>
</template>

<script>
// usage:
// <ApiField
//   label="Name"
//   storeAction="moduleName/exampleObjectUpdate"
//   storeUpdate="moduleName/refreshObject"
//   paramName="object_name_for_rails"
//   :object="objectInJs"
//   fieldName="name"
//   :storeFieldValue="object.name (from js)"
// >

// example store methods to work with this.
// storeAction:
// exampleObjectUpdate ({ dispatch, commit }, idFieldData) {
//   return new Promise((resolve, reject) => {
//     objectApi.update(idFieldData, object => {
//       resolve(object) !!! PASSING IN THE RESPONSE HERE IS VERY IMPORTANT
//     },
//     error => {
//       console.log('Something broke at the API');
//       reject()
//     })
//   })
// },

// // storeUpdate:
// refreshObject ({ dispatch, commit }, object) {
//   commit('refreshObject', object)
// }

// // ...the above commits:
// refreshObject (state, object) {
//   state.objects = [
//     ...state.objects.filter(o => o.id !== object.id),
//     object
//   ]
// },
//
// api method:
// update (idAndFields, callback, errorCallback) {
//   axios
//     .patch(`/api/object/${ idAndFields.id }.json`, idAndFields)
//     .then(response => {
//       callback(response.data)
//     })
//     .catch(error => {
//       errorCallback(error)
//     })
// },

export default {
  props: [
    "hint",
    "disabled",
    "storeAction",
    "storeUpdate",
    "label",
    "fieldName",
    "object",
    "mask",
    "fieldType",
    "prefix",
    "storeFieldValue",
    "paramName",
    "rows",
    "items",
    "itemText",
    "itemValue",
    "clearable",
    "type",
    "box"
  ],
  computed: {
    fieldValue: {
      get: function() {
        return this.storeFieldValue;
      },
      set: function(newValue) {
        var newValue = newValue;
        if (newValue === undefined) {
          var newValue = null;
        }
        this.loading = true;
        this.$store
          .dispatch(this.storeAction, {
            id: this.object.id,
            [this.paramName]: { [this.fieldName]: newValue } // object-name-nested params for rails
          })
          .then(res => {
            if (this.storeUpdate) {
              this.$store.dispatch(this.storeUpdate, res);

              // Necessary for updating manager data
              // /hr/managers/:id
              // Does nothing if called from other ApiField uses
              this.$emit("updateManager", res);
            }
            this.errors = [];
            this.successes.push("Saved!");
            this.successClear = setInterval(() => {
              this.wipeSuccesses();
            }, 1500);
          })
          .catch(res => {
            this.errors.push("Couldn't be saved");
            // reset the value?
          })
          .finally(res => {
            this.loading = false;
          });
      }
    }
  },

  methods: {
    wipeSuccesses: function() {
      this.successes = [];
    }
  },

  data: function() {
    return {
      loading: false,
      fallback: "",
      errors: [],
      successes: [],
      dateMenu: false,
      timeMenu: false
    };
  }
};
</script>
